import { TableRow } from './../search/models/table-row.model';
import { DataTableConfig } from '../search/models/data-table-config';
import { SEARCH_DEFAULT_PAGE_SIZE, SEARCH_DEFAULT_COLUMN_COUNT } from '../search/search-constants';
import { SearchParams } from '../search/models/search-params.model';
import { FormPageConfig } from './form-page-config';

export class SearchableEntityConfig {

    private pageTitle: string;
    private itemLabel: string;
    private breadCrumbPrefix: string;

    private primarySearchParams: SearchParams;
    private detailSearchParams: SearchParams;

    private entityContextObject: any;
    private relationshipParams: any;
    private relationshipCache: any;
    private targetContextIdentifier: string;

    private searchPageRoute: string;
    private detailPageRoute: string;
    private allowStateSave: boolean;

    public useAdvancedTable = false;
    public showSearchFilter = true;
    public showDetailTable = true;

    // For displaying line items in details screen
    public showDetailLineItems = true;

    public formPageConfig: FormPageConfig;

    constructor(public type: string) {
        this.entityContextObject = {
            default: {}
        };
    }

    public getType() {
        return this.type;
    }

    public getPageTitle() {
        return this.pageTitle;
    }

    public setPageTitle(pageTitle) {
        return this.pageTitle = pageTitle;
    }

    public getItemLabel() {
        return this.itemLabel;
    }

    public setItemLabel(itemLabel) {
        return this.itemLabel = itemLabel;
    }

    public getBreadCrumbPrefix() {
        return this.breadCrumbPrefix;
    }

    public setBreadCrumbPrefix(breadCrumbPrefix) {
        return this.breadCrumbPrefix = breadCrumbPrefix;
    }

    public initializePrimarySearchForTableId(primaryDataTableId: string, primaryIdProperty: string) {
        this.primarySearchParams = new SearchParams();
        this.primarySearchParams.tableId = primaryDataTableId;
        this.primarySearchParams.idProperty = primaryIdProperty;
    }

    public initializeDetailSearchForTableId(detailDataTableId: string, detailIdProperty: string) {
        this.detailSearchParams = new SearchParams();
        this.detailSearchParams.tableId = detailDataTableId;
        this.detailSearchParams.idProperty = detailIdProperty;
    }

    public getEntityContextObject(): any {
        return this.entityContextObject;
    }

    public getEntityContext(dynamicContext: string): any {
        return this.entityContextObject[dynamicContext];
    }

    public removeEntityContext(dynamicContext: string) {
        delete this.entityContextObject[dynamicContext];
    }

    public initializeSearchForEntityContext(dynamicContext: string, entityContextParams: any) {
        const entityContext = this.entityContextObject[dynamicContext] = {};
        entityContext['searchParams'] = new SearchParams();
        entityContext['searchParams'].tableId = entityContextParams.dataTableId;
        entityContext['searchParams'].idProperty = entityContextParams.dataTableIdProperty;
        entityContext['searchParams'].isFullFetch = entityContextParams.isFullFetch;
        entityContext['searchParams'].tableEditor = entityContextParams.tableEditor;
        entityContext['searchParams'].dataTableConfig = new DataTableConfig();
        if (entityContextParams.isSelectedCellWidth) {
            entityContext['searchParams'].dataTableConfig
                .isSelectedCellWidth = entityContextParams.isSelectedCellWidth;
        }
        if (entityContextParams.additionalSelectionIcon) {
            entityContext['searchParams']
                .dataTableConfig.additionalSelectionIcon
                    = entityContextParams.additionalSelectionIcon;
        }
        if (entityContextParams.isSingleSelect) {
            entityContext['searchParams'].isSingleSelect = true;
        }
        if (entityContextParams.paginateLocally) {
            entityContext['searchParams'].paginateLocally = true;
        }
        Object.keys(entityContextParams.customPropertyObject).forEach((customProperty: any) => {
                entityContext[customProperty]
                    = entityContextParams.customPropertyObject[customProperty];
            });
    }

    public initializeRelationshipCache(relationshipParams: any) {
        this.relationshipParams = relationshipParams;
        this.relationshipCache = {};
        Object.keys(relationshipParams).forEach((subContext: string) => {
                const targetContext: string = relationshipParams[subContext].targetContext;
                if (!this.relationshipCache[targetContext]) {
                    this.relationshipCache[targetContext] = {};
                }
            });
    }

    public createCachedRelationship($event: any, isUnassign?: boolean) {
        const relationshipParam: any = this.relationshipParams[$event.rKey];
        const relationshipDefinition: any = this.relationshipCache[relationshipParam.targetContext];
        const relationshipCount: number
            = Object.keys(this.relationshipCache[relationshipParam.targetContext]).length;
        let leftKeyValue = '';
        relationshipParam.leftKeys
            .map((leftKey: string) => {
                leftKeyValue = $event.rPageRow.cells[leftKey].cVal;
            });
        let rightKeyValue = '';
        relationshipParam.rightKeys
            .map((rightKey: string) => {
                rightKeyValue += '-' + $event.rPageRow.cells[rightKey].cVal;
            });
        if (leftKeyValue && rightKeyValue) {
            const relationshipRow: TableRow = new TableRow();
            relationshipRow['rIdx'] = relationshipCount + 1;
            relationshipRow.isSelected = false;
            relationshipRow.isEdited = true;
            relationshipRow.cells = {};
            Object.keys($event.rPageRow.cells).forEach((cName: string) => {
                    relationshipRow.cells[cName] = $event.rPageRow.cells[cName];
                });
            if (isUnassign) {
                relationshipRow.cells['qtyLocMin'] = { cName: 'qtyLocMin', cVal: 0 };
                relationshipRow.cells['qtyLocMax'] = { cName: 'qtyLocMax', cVal: 0 };
                relationshipRow.cells['locType'] = { cName: 'locType', cVal: 'OTHER' };
            } else {
                Object.keys(relationshipParam.clientProperties).forEach((cName: string) => {
                        relationshipRow.cells[cName]
                            = {
                                'cName': cName,
                                'cVal': (relationshipParam.clientProperties[cName].cVal)
                            };
                    });
            }
            const relationshipKey: string = leftKeyValue + rightKeyValue;
            relationshipDefinition[relationshipKey] = relationshipRow;
        }
    }

    public clearRelationship(dynamicContext: string, relationshipKey?: string) {
        const relationshipDefinition: any = this.relationshipCache[dynamicContext];
        if (relationshipKey) {
            delete relationshipDefinition[relationshipKey];
        } else {
            this.relationshipCache[dynamicContext] = {};
        }
    }

    public getRelationshipCache(dynamicContext?: string): any {
        return (dynamicContext) ? this.relationshipCache[dynamicContext] : this.relationshipCache;
    }

    public setTargetContextIdentifier(targetContextIdentifier: string) {
        this.targetContextIdentifier = targetContextIdentifier;
    }

    public getTargetContextIdentifier(): string {
        return this.targetContextIdentifier;
    }

    public getRelationshipParams(): any {
        return this.relationshipParams;
    }

    public getSearchPageRoute() {
        return this.searchPageRoute;
    }

    public setSearchPageRoute(searchPageRoute) {
        return this.searchPageRoute = searchPageRoute;
    }

    public getDetailPageRoute() {
        return this.detailPageRoute;
    }

    public setDetailPageRoute(detailPageRoute) {
        return this.detailPageRoute = detailPageRoute;
    }

    public getAllowStateSave() {
        return this.allowStateSave;
    }

    public setAllowStateSave(allowStateSave) {
        return this.allowStateSave = allowStateSave;
    }

    public getUseAdvancedTable() {
        return this.useAdvancedTable;
    }

    public setUseAdvancedTable(useAdvancedTable) {
        return this.useAdvancedTable = useAdvancedTable;
    }

    public getPrimarySearchParams() {
        return this.primarySearchParams;
    }

    public setPrimarySearchParams(primarySearchParams) {
        return this.primarySearchParams = primarySearchParams;
    }

    public getDetailSearchParams() {
        return this.detailSearchParams;
    }

    public setDetailSearchParams(detailSearchParams) {
        return this.detailSearchParams = detailSearchParams;
    }

    public getShowDetailLineItems() {
        return this.showDetailLineItems;
    }

    public setShowDetailLineItems(showDetailLineItems) {
        return this.showDetailLineItems = showDetailLineItems;
    }

    /**
     * This method initializes the configuration for the ASN Template screen
     */
    public initializeConfigForTemplate1() {
        this.showSearchFilter = true;
        this.primarySearchParams.isFullFetch = true; // setting fullfetch to true to display all searchable fields in the table LFWM-1411
        this.primarySearchParams.defaultPageSize = SEARCH_DEFAULT_PAGE_SIZE;
        this.primarySearchParams.columnCount = SEARCH_DEFAULT_COLUMN_COUNT;
        this.createDefaultPrimaryTableConfig();

        this.detailSearchParams.defaultPageSize = SEARCH_DEFAULT_PAGE_SIZE;
        this.detailSearchParams.isFullFetch = true;
        this.createDefaultDetailTableConfig();

        this.formPageConfig = new FormPageConfig();
        this.formPageConfig.layoutType = 'default-accordion';
    }

    /**
     * This method initializes the configuration for search screen with a filter
     */
    public initializeConfigForTemplate2() {
        this.showSearchFilter = true;

        this.primarySearchParams.isFullFetch = true;
        this.primarySearchParams.paginateLocally = false;
        this.primarySearchParams.defaultPageSize = SEARCH_DEFAULT_PAGE_SIZE;
        this.primarySearchParams.autoGenerateIdProperty = false;

        const primaryTableConfig: DataTableConfig = new DataTableConfig();
        primaryTableConfig.editable = true;
        primaryTableConfig.showIdAsAHyperLink = false;
        primaryTableConfig.supportFiltering = true;
        this.primarySearchParams.dataTableConfig = primaryTableConfig;
    }

    /**
     * This method initializes the configuration for search screen without a filter
     */
    public initializeConfigForTemplate3() {
        this.showSearchFilter = false;

        this.primarySearchParams.isFullFetch = true;
        this.primarySearchParams.paginateLocally = false;
        this.primarySearchParams.defaultPageSize = SEARCH_DEFAULT_PAGE_SIZE;
        this.primarySearchParams.autoGenerateIdProperty = false;

        const primaryTableConfig: DataTableConfig = new DataTableConfig();
        primaryTableConfig.editable = true;
        primaryTableConfig.showIdAsAHyperLink = false;
        primaryTableConfig.supportFiltering = true;
        this.primarySearchParams.dataTableConfig = primaryTableConfig;
    }

    /**
    * This method initializes the configuration for search screen with filter in view mode
    */
    public initializeConfigForTemplate4() {
        this.showSearchFilter = true;

        this.primarySearchParams.isFullFetch = true;
        this.primarySearchParams.paginateLocally = false;
        this.primarySearchParams.defaultPageSize = SEARCH_DEFAULT_PAGE_SIZE;

        const primaryTableConfig: DataTableConfig = new DataTableConfig();
        primaryTableConfig.editable = false;
        primaryTableConfig.showIdAsAHyperLink = true;
        primaryTableConfig.supportFiltering = true;
        this.primarySearchParams.dataTableConfig = primaryTableConfig;
    }
    /**
     * This method initializes the configuration for screens without search
     */
    public initializeConfigForTemplate5() {
        this.showSearchFilter = false;

        this.primarySearchParams.isFullFetch = true;
        this.primarySearchParams.paginateLocally = false;
        this.primarySearchParams.defaultPageSize = SEARCH_DEFAULT_PAGE_SIZE;
        this.primarySearchParams.autoGenerateIdProperty = false;

        this.formPageConfig = new FormPageConfig();
        this.formPageConfig.layoutType = 'default-accordion';
    }
    /**
    * This method initializes the configuration for details screen without line items
    */
    public initializeConfigDetailWithoutLineItem() {
        this.showSearchFilter = true;
        this.primarySearchParams.isFullFetch = true;
        this.showDetailLineItems = false;
        this.primarySearchParams.defaultPageSize = SEARCH_DEFAULT_PAGE_SIZE;
        this.primarySearchParams.columnCount = SEARCH_DEFAULT_COLUMN_COUNT;
        this.createDefaultPrimaryTableConfig();

        this.formPageConfig = new FormPageConfig();
        this.formPageConfig.layoutType = 'default-accordion';
    }

    public createDefaultPrimaryTableConfig() {
        const primaryTableConfig: DataTableConfig = new DataTableConfig();
        primaryTableConfig.editable = false;
        primaryTableConfig.showIdAsAHyperLink = true;
        primaryTableConfig.supportFiltering = true;
        this.primarySearchParams.dataTableConfig = primaryTableConfig;
    }

    public createDefaultDetailTableConfig() {
        const detailTableConfig: DataTableConfig = new DataTableConfig();
        detailTableConfig.editable = true;
        detailTableConfig.showIdAsAHyperLink = false;
        detailTableConfig.supportFiltering = true;
        this.detailSearchParams.dataTableConfig = detailTableConfig;
    }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/1_SCE_CI_CD_SIT/src/app/shared/models/searchable-entity-config.ts